import { Search } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useInfinitePosts } from "@/entities/post"
import { useDurations } from "@/entities/taxonomy"
import { DurationFilterDialog, type SortOption } from "@/features/filter-posts"
import { PostsSearchBar, useRecentQueries } from "@/features/search-posts"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { useUrlState } from "@/shared/hooks/use-url-state"
import { EmptyState } from "@/shared/ui/empty-state"
import { PageTransition } from "@/shared/ui/page-transition"
import { SectionHeading } from "@/shared/ui/section-heading"
import { PostsFeed } from "@/widgets/posts-feed"

const CHIP = "rounded-full bg-white/[0.06] px-3 py-1.5 text-xs font-medium ring-1 ring-white/10"

export function SearchPage() {
	const [searchQuery, setSearchQuery] = useUrlState("q")
	const [searchInput, setSearchInput] = useState(searchQuery)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const { queries: recentQueries, addQuery, clearQueries } = useRecentQueries()
	const { triggerHaptic } = useHaptic()

	const [durationParam, setDurationParam] = useUrlState("duration")
	const selectedDurationIds = useMemo(
		() => (durationParam ? durationParam.split(",") : []),
		[durationParam],
	)
	const setSelectedDurationIds = (value: string[] | ((previous: string[]) => string[])) =>
		setDurationParam((typeof value === "function" ? value(selectedDurationIds) : value).join(","))
	const [sortParam, setSortBy] = useUrlState("sort", "newest")
	const sortBy: SortOption = sortParam === "oldest" ? "oldest" : "newest"
	const { data: durations = [] } = useDurations()

	const hasCriteria = Boolean(searchQuery) || selectedDurationIds.length > 0

	const {
		data,
		isLoading,
		isError,
		error,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfinitePosts(
		{
			isActive: true,
			search: searchQuery || undefined,
			sortBy,
			durationIds: selectedDurationIds.length > 0 ? selectedDurationIds.join(",") : undefined,
		},
		{ enabled: hasCriteria },
	)
	const posts = useMemo(() => data?.pages.flat() ?? [], [data])

	// biome-ignore lint/correctness/useExhaustiveDependencies: фокус только при открытии вкладки
	useEffect(() => {
		if (!searchQuery) searchInputRef.current?.focus()
	}, [])

	const commitSearch = () => {
		const trimmed = searchInput.trim()
		setSearchQuery(trimmed)
		if (trimmed) addQuery(trimmed)
	}

	const pickQuery = (query: string) => {
		triggerHaptic("light")
		setSearchInput(query)
		setSearchQuery(query)
		addQuery(query)
		searchInputRef.current?.blur()
	}

	const resetAll = () => {
		triggerHaptic("light")
		setSearchInput("")
		setSearchQuery("")
		setSelectedDurationIds([])
		setSortBy("newest")
	}

	const activeFilters = [
		searchQuery ? `Поиск: ${searchQuery}` : null,
		...selectedDurationIds.map((id) => durations.find((item) => item.id === id)?.name ?? null),
		sortBy === "oldest" ? "Сначала старые" : null,
	].filter((label): label is string => Boolean(label))

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_24%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto max-w-3xl px-3 pt-5 pb-5 sm:px-4">
					<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
						Каталог
					</p>
					<h1 className="mt-1 text-3xl font-bold tracking-tight">Поиск</h1>
					<p className="mt-2 text-sm leading-snug text-muted-foreground">
						По названию, описанию и длительности материала.
					</p>
				</div>
			</header>

			<div className="container mx-auto max-w-3xl px-3 pb-6 sm:px-4">
				<PostsSearchBar
					value={searchInput}
					onChange={(value) => {
						setSearchInput(value)
						if (!value) setSearchQuery("")
					}}
					onSearch={commitSearch}
					inputRef={searchInputRef}
					autoSearch
					recentQueries={recentQueries}
					onPickRecent={pickQuery}
					trailing={
						<DurationFilterDialog
							durations={durations}
							selectedDurationIds={selectedDurationIds}
							sortBy={sortBy}
							onDurationToggle={(durationId) =>
								setSelectedDurationIds((prev) =>
									prev.includes(durationId)
										? prev.filter((id) => id !== durationId)
										: [...prev, durationId],
								)
							}
							onSortChange={setSortBy}
							onClearFilters={() => setSelectedDurationIds([])}
						/>
					}
				/>
			</div>

			<div className="container mx-auto max-w-3xl px-3 pb-6 sm:px-4 lg:pb-8">
				{hasCriteria ? (
					<>
						<div className="mb-5 flex flex-wrap items-center gap-2">
							{activeFilters.map((label) => (
								<span key={label} className={CHIP}>
									{label}
								</span>
							))}
							<button
								type="button"
								onClick={resetAll}
								className="text-xs font-medium text-primary transition-opacity active:opacity-70"
							>
								Сбросить
							</button>
						</div>

						<SectionHeading title="Результаты" />
						<PostsFeed
							posts={posts}
							isLoading={isLoading}
							isError={isError}
							error={error}
							onRetry={() => void refetch()}
							hasNextPage={hasNextPage}
							isFetchingNextPage={isFetchingNextPage}
							onLoadMore={() => fetchNextPage()}
							emptyMessage="Ничего не нашлось"
							emptyAction={
								<button
									type="button"
									onClick={resetAll}
									className="text-sm font-medium text-primary transition-opacity active:opacity-70"
								>
									Сбросить поиск
								</button>
							}
						/>
					</>
				) : recentQueries.length > 0 ? (
					<section>
						<SectionHeading
							title="Вы искали"
							trailing={
								<button
									type="button"
									onClick={() => {
										triggerHaptic("light")
										clearQueries()
									}}
									className="shrink-0 text-xs font-medium text-muted-foreground transition-opacity active:opacity-70"
								>
									Очистить
								</button>
							}
						/>
						<div className="flex flex-wrap gap-2">
							{recentQueries.map((query) => (
								<button
									key={query}
									type="button"
									onClick={() => pickQuery(query)}
									className={`${CHIP} inline-flex items-center gap-1.5 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-95`}
								>
									<Search className="size-3.5 text-muted-foreground" />
									{query}
								</button>
							))}
						</div>
					</section>
				) : (
					<EmptyState icon={Search} title="Что ищем?">
						Введите название материала или слово из описания.
					</EmptyState>
				)}
			</div>
		</PageTransition>
	)
}
